/**
 * SocialPulse API client.
 *
 * One place that knows how to talk to the backend: it serialises the shared
 * FilterQuery, unwraps the `{data, meta}` envelope, normalises errors, and
 * transparently falls back to a bundled offline snapshot so the product is
 * fully explorable in demo mode when the API is unreachable.
 *
 * See docs/contracts/API_CONTRACT.md for the authoritative endpoint list.
 */

import { getImpersonationToken, isImpersonating } from '../store/impersonation'
import { getTenantCsrfToken, isTenantAuthenticated } from '../store/tenantAuth'

export const API_BASE = import.meta.env?.VITE_API_BASE ?? '/api/v1'
const TIMEOUT_MS = Number(import.meta.env?.VITE_API_TIMEOUT ?? 15000)
// A signed-in tenant session reads its own (possibly two-year, 8000-post)
// dataset; a cold, uncached analytics call on the shared production host can
// legitimately take well over 15s. Timing it out would only surface an error
// (the snapshot is never served to a session, see `snapshotAllowed`), so give
// session reads a much longer budget.
const SESSION_TIMEOUT_MS = Number(import.meta.env?.VITE_API_SESSION_TIMEOUT ?? 90000)

/**
 * Whether a failed request may be answered from the bundled offline snapshot.
 *
 * The snapshot is ONE fixed telecom dataset (Syriatel/MTN brands, its own
 * date range). It exists so the unauthenticated demo stays explorable with no
 * API. For a signed-in tenant (or an impersonating platform admin) it would
 * silently mix another company's numbers into a bank's or airline's
 * dashboard next to its live panels -- so a session gets the real error and
 * the page's retry state instead.
 */
function snapshotAllowed() {
  try {
    return !isTenantAuthenticated() && !isImpersonating()
  } catch {
    return true
  }
}

export class ApiError extends Error {
  constructor(message, { status = 0, endpoint = '', detail = null } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.endpoint = endpoint
    this.detail = detail
  }
}

/* -------------------------------------------------------------- connectivity */

let connectionState = 'unknown' // 'live' | 'offline' | 'unknown'
const connectionListeners = new Set()

export function getConnectionState() {
  return connectionState
}

export function onConnectionChange(fn) {
  connectionListeners.add(fn)
  return () => connectionListeners.delete(fn)
}

function setConnectionState(next) {
  if (next === connectionState) return
  connectionState = next
  connectionListeners.forEach((fn) => fn(next))
}

/* ----------------------------------------------------------- query building */

const CSV_KEYS = new Set([
  'sources', 'topics', 'sentiments', 'intents', 'emotions',
  'languages', 'regions', 'authors', 'brands',
])

/**
 * Serialise a filter object into the contract's query-string shape.
 * Arrays become CSV, empty/null/undefined/`all` values are dropped so the URL
 * stays short and cache keys stay stable.
 */
export function buildQuery(params = {}) {
  const usp = new URLSearchParams()
  const keys = Object.keys(params).sort() // stable order => stable cache keys
  for (const key of keys) {
    const value = params[key]
    if (value === null || value === undefined || value === '') continue
    if (Array.isArray(value)) {
      if (value.length === 0) continue
      usp.set(key, CSV_KEYS.has(key) ? value.join(',') : value.join(','))
    } else if (typeof value === 'boolean') {
      if (value) usp.set(key, 'true')
    } else {
      if (value === 'all' && key !== 'bot_filter') continue
      usp.set(key, String(value))
    }
  }
  const qs = usp.toString()
  return qs ? `?${qs}` : ''
}

/* -------------------------------------------------------------- offline mode */

// The snapshot is a static asset the server can update at any time (a fresh
// export, a redeploy) -- both `frontend/nginx.conf` and the Vite dev server
// answer it with `Cache-Control: no-cache` plus an `ETag`/`Last-Modified`, so
// the server is always willing to tell a client whether its copy is stale.
// The client has to actually ask, though: we still memoise the parsed ~3MB
// payload in memory so the common case (many components reading the same
// snapshot within one page) doesn't refetch it on every render, but that
// memo now has a TTL. Once it expires, the next read re-hits the network --
// a cheap 304 if nothing changed, a fresh body if it did -- so an open tab
// converges on whatever the server is serving instead of pinning the first
// response it ever saw for the rest of the session.
const SNAPSHOT_TTL_MS = 60_000
let snapshotPromise = null
let snapshotFetchedAt = 0

async function fetchSnapshot() {
  return fetch(`${import.meta.env?.BASE_URL ?? '/'}snapshot.json`, { cache: 'no-cache' })
    .then((res) => (res.ok ? res.json() : null))
    .catch(() => null)
}

async function loadSnapshot() {
  const isStale = !snapshotPromise || Date.now() - snapshotFetchedAt > SNAPSHOT_TTL_MS
  if (isStale) {
    snapshotFetchedAt = Date.now()
    snapshotPromise = fetchSnapshot()
  }
  return snapshotPromise
}

/**
 * Force the next snapshot read to revalidate against the server instead of
 * serving the in-memory copy, regardless of {@link SNAPSHOT_TTL_MS}. Wired to
 * `visibilitychange`/`online` below so a backgrounded or reconnecting tab
 * never has to wait out the TTL; also called right after a live request
 * fails for the first time in a session, so the very first fallback is never
 * served from a copy fetched (and potentially outdated) long before the API
 * actually went down.
 */
export function invalidateSnapshot() {
  snapshotPromise = null
  snapshotFetchedAt = 0
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', invalidateSnapshot)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') invalidateSnapshot()
  })
}

/** Test seam: lets a suite inject a snapshot without hitting the network. */
export function __setSnapshot(snapshot) {
  snapshotPromise = Promise.resolve(snapshot)
  snapshotFetchedAt = Date.now()
}

/**
 * Resolve an endpoint from the offline snapshot. The snapshot is keyed by the
 * bare path (e.g. `/overview`); paginated endpoints are re-sliced client-side
 * so paging still works without a server.
 */
async function fromSnapshot(path, params) {
  const snap = await loadSnapshot()
  if (!snap) return null
  const key = path.startsWith('/') ? path : `/${path}`
  let payload = snap[key]
  if (payload === undefined) {
    // `/posts/p_0001` style detail routes fall back to a lookup in `/posts`.
    const [, head, id] = key.split('/')
    if (head === 'posts' && id && snap['/posts']?.items) {
      const hit = snap['/posts'].items.find((p) => p.id === id)
      if (hit) return { data: { post: hit, similar: [] }, meta: { offline: true } }
    }
    return null
  }
  payload = structuredClone(payload)
  if (payload && Array.isArray(payload.items) && params?.page) {
    const size = Number(params.page_size) || 25
    const page = Number(params.page) || 1
    const total = payload.items.length
    payload.items = payload.items.slice((page - 1) * size, page * size)
    return {
      data: payload,
      meta: { offline: true, total, page, page_size: size, total_pages: Math.ceil(total / size) },
    }
  }
  return { data: payload, meta: { offline: true } }
}

/* ------------------------------------------------------------------ transport */

/**
 * Perform a request against the API, falling back to the offline snapshot when
 * the network is unavailable or the server errors.
 *
 * @returns {Promise<{data: any, meta: object}>}
 */
export async function request(path, { params, method = 'GET', body, signal, raw = false, headers } = {}) {
  const url = `${API_BASE}${path}${method === 'GET' ? buildQuery(params) : ''}`
  const controller = new AbortController()
  const session = !snapshotAllowed()
  const timer = setTimeout(() => controller.abort(), session ? SESSION_TIMEOUT_MS : TIMEOUT_MS)
  if (signal) signal.addEventListener('abort', () => controller.abort(), { once: true })

  try {
    const res = await fetch(url, {
      method,
      signal: controller.signal,
      // Required so the browser attaches the httpOnly session cookie
      // (`sp_platform_session` / `sp_tenant_session`) to same-origin/CORS
      // calls — see backend/core/middleware.py's `allow_credentials=True`.
      // Harmless for the unauthenticated analytics endpoints too.
      credentials: 'include',
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        // Sprint 7: while a platform admin is impersonating a tenant user
        // (`store/impersonation.js`), every request carries that bearer
        // token so the whole app (not just the ticketing endpoints) acts as
        // the impersonated user. `core.deps_rbac.get_current_tenant_user`
        // checks the tenant session cookie first, this header second, so
        // this never overrides a real tenant user's own cookie session.
        ...(getImpersonationToken() ? { Authorization: `Bearer ${getImpersonationToken()}` } : {}),
        // Every non-GET call authenticated via the tenant session cookie
        // (i.e. not impersonation, which authenticates via the Authorization
        // header above and is exempt — see `backend/core/csrf.py`) must echo
        // the `sp_tenant_csrf` cookie's value back on this header, or
        // `enforce_csrf` rejects it with 403 regardless of which `/api/v1`
        // route it hits. `store/tenantAuth.js` is this app's only cookie
        // session today (Command Center et al. are always tenant-scoped),
        // same contract as `lib/tenantUserApi.js`'s `csrfHeaders()`.
        ...(method !== 'GET' && !getImpersonationToken() && getTenantCsrfToken()
          ? { 'X-CSRF-Token': getTenantCsrfToken() }
          : {}),
        ...(headers || {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    })
    if (!res.ok) {
      let detail = null
      try { detail = await res.json() } catch { /* non-JSON error body */ }
      // Our API's errors always carry `detail` or `code` (see core/errors.py).
      // A 404/405 without that shape did not come from the API at all — it is a
      // static host answering for a path it does not serve — so it counts as
      // "unreachable" and falls through to the snapshot below. Every other
      // status is a real answer and must surface to the caller.
      const routeMissing = res.status === 404 || res.status === 405
      const answeredByApi = !routeMissing
        || Boolean(detail && (detail.detail !== undefined || detail.code !== undefined))
      throw new ApiError(detail?.detail || `Request failed (${res.status})`, {
        status: answeredByApi ? res.status : 0, endpoint: path, detail,
      })
    }
    setConnectionState('live')
    if (raw) return res
    const json = await res.json()
    // Tolerate both enveloped and bare payloads.
    return json && typeof json === 'object' && 'data' in json ? json : { data: json, meta: {} }
  } catch (err) {
    // A 4xx is a real answer from a live server — surface it, don't fake data.
    if (err instanceof ApiError && err.status >= 400 && err.status < 500) throw err

    // A cancellation requested by the *caller* (a component re-rendering with
    // new filters, or unmounting) says nothing about the server's health.
    // Serving the snapshot here would flip the whole app into "Snapshot mode"
    // on every filter change, so just propagate the abort untouched. Only an
    // abort from our own TIMEOUT_MS timer (caller signal still live) counts
    // as the network being unavailable.
    if (err?.name === 'AbortError' && signal?.aborted) throw err

    // The moment a live call fails, make sure the fallback we're about to
    // serve reflects the server's *current* snapshot, not whatever this tab
    // happened to fetch (and cache for up to SNAPSHOT_TTL_MS) while the API
    // was still reachable.
    if (session) {
      throw err instanceof ApiError
        ? err
        : new ApiError(err?.name === 'AbortError' ? 'Request timed out' : (err?.message || 'Network error'), {
            endpoint: path,
          })
    }
    if (connectionState !== 'offline') invalidateSnapshot()
    const fallback = await fromSnapshot(path, params)
    if (fallback) {
      setConnectionState('offline')
      return fallback
    }
    setConnectionState('offline')
    throw err instanceof ApiError
      ? err
      : new ApiError(err?.name === 'AbortError' ? 'Request timed out' : (err?.message || 'Network error'), {
          endpoint: path,
        })
  } finally {
    clearTimeout(timer)
  }
}

/** Convenience: return just the payload. */
export async function get(path, params, signal) {
  const { data } = await request(path, { params, signal })
  return data
}

/** Convenience: return payload and meta together (for paginated tables). */
export async function getPage(path, params, signal) {
  return request(path, { params, signal })
}

export async function post(path, body, params) {
  const { data } = await request(path, { method: 'POST', body, params })
  return data
}

export async function put(path, body) {
  const { data } = await request(path, { method: 'PUT', body })
  return data
}

export async function del(path) {
  const { data } = await request(path, { method: 'DELETE' })
  return data
}

/** Trigger a CSV download honouring the current filters. */
export async function downloadCsv(path, params, filename = 'socialpulse-export.csv') {
  const res = await request(path, { params, raw: true })
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
